import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets.ts';
import { useMemo } from 'react';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { RequiredTargetingSectionKeys } from '../context/types.ts';
import { useFormatInitialState } from '../hooks/initialState/useFormatInitialState.tsx';
import { useReadOnlyFormatInitialState } from '../hooks/readOnlyInitialState/useReadOnlyFormatInitialState.tsx';
import { useSyncInitialTargeting } from '../hooks/useSyncInitialTargeting.ts';
import { TargetingPresetSection } from '../TargetingPresetSection/TargetingPresetSection.tsx';
import { useCTVPopup } from './components/CTVPopup/hooks/useCTVPopup.tsx';
import { useAllSectionsLoaded } from './hooks/useAllSectionsLoaded.ts';

type FormSectionProps = {
  targetingParams?: RequiredTargetingFormType | null;
  readOnly?: boolean;
};

export const FormatSection = ({ readOnly, targetingParams }: FormSectionProps) => {
  const { useAdPlacementOptions } = useTargetingContext();
  const { isAdPlacementLoading, adPlacement: adPlacementMap } = useAdPlacementOptions();
  const allSectionsLoaded = useAllSectionsLoaded();

  const readOnlyInitialState = useReadOnlyFormatInitialState(targetingParams);
  const editableInitialState = useFormatInitialState();
  const initialState = useMemo(
    () => (readOnly ? readOnlyInitialState : editableInitialState),
    [readOnly, editableInitialState, readOnlyInitialState]
  );

  useSyncInitialTargeting({
    isLoading: isAdPlacementLoading,
    sectionKey: RequiredTargetingSectionKeys.FORMAT,
    initialState,
    externalOptionsMap: adPlacementMap,
  });

  useCTVPopup();

  return (
    <TargetingPresetSection
      name="ad_placement"
      sectionKey={RequiredTargetingSectionKeys.FORMAT}
      label={initialState.label}
      elements={initialState.elements}
      isLoading={!allSectionsLoaded}
      noMarginTop
      readOnly={!!readOnly}
    />
  );
};
